const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8081/api';

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// --- New share preview types ---

export interface AssetShareResultItem {
  id: string;
  imageUrl: string;
  imageHash: string;
  role: 'primary' | 'alternate';
}

export interface AssetShareStylingGroup {
  state: string;
  results: AssetShareResultItem[];
}

export interface AssetShareFaceGroup {
  casting: {
    imageUrl: string;
    imageHash: string;
  };
  stylings: AssetShareStylingGroup[];
}

export interface AssetShareCharacter {
  name: string;
  faceGroups: AssetShareFaceGroup[];
}

export interface AssetSharePreviewData {
  label: string;
  projectName: string;
  expiresAt: string;
  expired?: boolean;
  readOnly?: boolean;
  characters: AssetShareCharacter[];
}

export interface AssetShareComment {
  id: string;
  shareLinkId: string;
  category: string;
  assetName: string;
  assetState: string;
  assetImageId: string;
  content: string;
  createdAt: string;
}

export interface AssetShareImageInfo {
  imageUrl: string;
  characterName: string;
  category: string;
  state: string;
  role: string;
}

export interface AssetShareLinkInfo {
  id: string;
  projectId: string;
  code: string;
  label: string;
  expiresAt: string;
  maxViews: number;
  viewCount: number;
  commentCount: number;
  createdAt: string;
}

// --- Public (no auth) ---

export async function getAssetSharePreview(code: string): Promise<AssetSharePreviewData> {
  const res = await fetch(`${API_BASE}/asset-share/${encodeURIComponent(code)}`, {
    headers: authHeaders(),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function getAssetShareComments(code: string): Promise<AssetShareComment[]> {
  const res = await fetch(`${API_BASE}/asset-share/${encodeURIComponent(code)}/comments`, {
    headers: authHeaders(),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `HTTP ${res.status}`);
  }
  const data = await res.json();
  return data.comments || [];
}

export async function createAssetShareComment(
  code: string,
  comment: { category: string; assetName: string; assetState: string; assetImageId: string; content: string }
): Promise<AssetShareComment> {
  const res = await fetch(`${API_BASE}/asset-share/${encodeURIComponent(code)}/comments`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(comment),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `HTTP ${res.status}`);
  }
  return res.json();
}

// --- Authenticated ---

export async function createAssetShareLink(
  projectId: string,
  options: { label: string; hours?: number; maxViews?: number }
): Promise<{ code: string; label: string; expiresAt: string; maxViews: number }> {
  const res = await fetch(`${API_BASE}/projects/${projectId}/asset-share`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(options),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function listAssetShareLinks(projectId: string): Promise<AssetShareLinkInfo[]> {
  const res = await fetch(`${API_BASE}/projects/${projectId}/asset-shares`, {
    headers: authHeaders(),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `HTTP ${res.status}`);
  }
  const data = await res.json();
  return data.links || [];
}

export async function getShareLinkComments(
  projectId: string,
  shareId: string
): Promise<{ comments: AssetShareComment[]; imageMap: Record<string, AssetShareImageInfo> }> {
  const res = await fetch(`${API_BASE}/projects/${projectId}/asset-shares/${shareId}/comments`, {
    headers: authHeaders(),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `HTTP ${res.status}`);
  }
  const data = await res.json();
  return { comments: data.comments || [], imageMap: data.imageMap || {} };
}
